import legacyArticles from './legacyBlogData.json';
import legacyImageMap from './legacyImageMap.json';

export const featured = {
  slug: 'consejos-pronunciacion-ingles',
  img: '/media/poster-presentacion.webp',
  cat: 'Pronunciación',
  title: '10 Consejos para Mejorar tu Pronunciación en Inglés',
  excerpt: 'Descubre las técnicas más efectivas para hablar inglés con más confianza. Desde el uso de recursos online hasta consejos prácticos de nuestros profesores.',
  author: 'Caroline Franks',
  authorRole: 'Fundadora & Profesora',
  avatar: '/media/caroline-avatar.webp',
  date: '15 Mar 2024',
  readTime: '8 min',
  keywords: 'pronunciación inglés, hablar inglés, mejorar pronunciación',
  body: `
## Por qué la pronunciación importa tanto

La pronunciación es la diferencia entre ser comprendido y tener que repetir cada frase. Muchos estudiantes de inglés descuidan este aspecto, enfocándose solo en gramática y vocabulario, y luego se frustran cuando los hablantes nativos no los entienden.

En Speak English Now llevamos más de 10 años ayudando a estudiantes de todos los niveles, y hemos identificado los errores más comunes y las técnicas que realmente funcionan.

## 1. Escucha activa antes de hablar

Antes de intentar reproducir un sonido, necesitas escucharlo correctamente. Dedica tiempo a escuchar inglés nativo: podcasts, series, películas. No como entretenimiento pasivo, sino prestando atención consciente a cómo suenan las palabras.

**Tip de Caroline:** Elige una serie que te guste y mírala con subtítulos en inglés. Pausa cuando escuches una pronunciación que no esperabas y repítela en voz alta.

## 2. Domina los sonidos que no existen en español

El inglés tiene varios fonemas que no existen en español:

- **La "th"** (ð/θ): coloca la lengua entre los dientes. "Think", "the", "this".
- **La vocal "schwa"** (ə): el sonido más común del inglés, como en "about", "butter".
- **La "r" americana**: pronunciada con la lengua curvada hacia atrás, sin vibración.
- **La vocal corta "æ"**: como en "cat", "man", "hat". Más abierta que nuestra "a".

## 3. Usa el IPA (Alfabeto Fonético Internacional)

Cada entrada en un diccionario inglés incluye la transcripción fonética. Aprende los símbolos básicos del IPA y podrás pronunciar cualquier palabra nueva correctamente desde el primer momento.

## 4. Practica con trabalenguas (tongue twisters)

Los trabalenguas son herramientas excepcionales para entrenar los músculos de la boca. Empieza despacio y aumenta la velocidad gradualmente:

- *"She sells seashells by the seashore"*
- *"How much wood would a woodchuck chuck"*
- *"Peter Piper picked a peck of pickled peppers"*

## 5. Grábate a ti mismo

La mayoría de personas odia escuchar su propia voz, pero es una de las herramientas más eficaces. Graba un párrafo en inglés, escúchalo y compara con un nativo. Identifica los sonidos que difieren.

## 6. Shadowing: repite mientras escuchas

La técnica del *shadowing* consiste en repetir lo que escuchas casi en tiempo real, imitando la entonación, el ritmo y los sonidos. Es la misma técnica que usan los actores y locutores profesionales.

## 7. Trabaja el ritmo y el estrés de la palabra

El inglés es una lengua de **estrés acentual**: algunas sílabas son más fuertes que otras, y los hablantes nativos reducen las palabras no acentuadas. Por ejemplo, "I want to go" suena como "I wanna go" en conversación rápida.

## 8. Aprende las contracciones naturales

Los hablantes nativos no dicen "I am going to", dicen "I'm gonna". No dicen "I do not know", dicen "I dunno". Aprende estas contracciones informales para sonar más natural.

## 9. Trabaja tu boca y cara

La pronunciación es física. El inglés requiere posiciones bucales distintas al español. Exagera los movimientos al practicar: abre más la boca, tensa los labios, mueve más la mandíbula.

## 10. Practica con un profesor nativo

Nada sustituye la retroalimentación en tiempo real de un nativo. Un buen profesor identificará exactamente qué estás haciendo mal y te dará ejercicios específicos para corregirlo.

En Speak English Now ofrecemos clases online con profesores nativos en grupos reducidos de máximo 8 estudiantes. Contáctanos y empieza a hablar inglés con confianza.

---

*¿Tienes alguna duda sobre pronunciación? Escríbenos por WhatsApp y te ayudamos.*
  `,
};

const currentArticles = [
  {
    slug: 'tiempos-verbales-ingles-guia-completa',
    img: '/media/caroline-libro.webp',
    cat: 'Gramática',
    title: 'Guía Completa de Tiempos Verbales en Inglés',
    excerpt: 'Aprende todos los tiempos verbales del inglés con ejemplos prácticos y ejercicios que te ayudarán a usarlos de forma natural.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '10 Mar 2024',
    readTime: '6 min',
    keywords: 'tiempos verbales inglés, presente perfecto, pasado simple, gramática inglesa',
    body: `
## Introducción a los tiempos verbales del inglés

Uno de los mayores retos para los hispanohablantes que aprenden inglés es dominar los tiempos verbales. Aunque el inglés tiene menos tiempos que el español, su uso puede ser muy diferente al que estamos acostumbrados.

## El presente simple (Simple Present)

Se usa para hábitos, rutinas y verdades universales.

**Estructura:** Sujeto + verbo (+ s/es en 3ª persona singular)

- I **work** every day. (Trabajo todos los días)
- She **speaks** English fluently. (Ella habla inglés con fluidez)
- The sun **rises** in the east. (El sol sale por el este)

**Errores comunes:** Olvidar la -s en tercera persona: "He work" ❌ → "He works" ✅

## El presente continuo (Present Continuous)

Se usa para acciones que ocurren ahora mismo o en un período temporal cercano.

**Estructura:** Sujeto + to be + verbo-ing

- I **am studying** right now. (Estoy estudiando ahora mismo)
- They **are meeting** tomorrow. (Se reúnen mañana — plan futuro)

## El pasado simple (Simple Past)

Para acciones completadas en un tiempo específico del pasado.

**Estructura:** Sujeto + verbo en pasado (regular: -ed, irregular: varía)

- I **worked** yesterday. (Trabajé ayer)
- She **went** to London last year. (Fue a Londres el año pasado)

## El presente perfecto (Present Perfect)

Este es el que más confunde a los hispanohablantes. Se usa para:
1. Acciones con relevancia en el presente
2. Experiencias de vida (sin tiempo específico)
3. Acciones recientes

**Estructura:** Sujeto + have/has + participio pasado

- I **have lived** in Spain for 10 years. (Llevo 10 años viviendo en España)
- She **has never visited** the USA. (Nunca ha visitado EE.UU.)
- They **have just arrived**. (Acaban de llegar)

**Clave:** Cuando hay un tiempo específico, usamos pasado simple. Sin tiempo específico → presente perfecto.

## El pasado continuo (Past Continuous)

Para acciones en progreso en un momento del pasado.

- I **was studying** when she called. (Estaba estudiando cuando llamó)

## El futuro (Future)

El inglés tiene varias formas de expresar el futuro:

- **Will:** Predicciones y decisiones espontáneas → "I will help you."
- **Going to:** Planes e intenciones → "I'm going to study tonight."
- **Present continuous:** Planes confirmados → "We're having dinner at 8."

## Consejo final

No intentes aprender todos los tiempos a la vez. Empieza con los más comunes (presente simple, pasado simple, presente continuo) y ve añadiendo los demás gradualmente mientras los ves en contexto.

En nuestras clases de inglés online trabajamos la gramática de forma práctica, en conversación real, no memorizando reglas de manual.
    `,
  },
  {
    slug: 'expandir-vocabulario-ingles-rapido',
    img: '/media/hermanos-torres.webp',
    cat: 'Vocabulario',
    title: 'Cómo Expandir tu Vocabulario en Inglés Rápidamente',
    excerpt: 'Técnicas probadas por psicólogos del aprendizaje para aprender y recordar más palabras en inglés cada semana.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '5 Mar 2024',
    readTime: '5 min',
    keywords: 'vocabulario inglés, aprender palabras inglés, técnicas vocabulario',
    body: `
## ¿Cuántas palabras necesitas saber?

Esta es la pregunta que todo estudiante de inglés se hace. La respuesta te sorprenderá: con solo **2.000 palabras** puedes mantener conversaciones cotidianas. Con **10.000** te manejas en casi cualquier situación. Y con **20.000+** te acercas al nivel de un nativo.

Esto significa que aprender vocabulario es absolutamente manejable. Solo necesitas el sistema correcto.

## La repetición espaciada (Spaced Repetition)

La ciencia del aprendizaje ha demostrado que la mejor forma de memorizar es revisar las palabras en intervalos crecientes: hoy, mañana, en 3 días, en una semana, en un mes.

**Apps recomendadas:** Anki (gratuita, personalizable), Duolingo (más gamificada), Quizlet.

## Aprende palabras en contexto, no aisladas

Nunca memorices listas de vocabulario. Es ineficaz porque el cerebro no retiene palabras sin contexto. Aprende cada palabra en una frase completa que tenga sentido para ti.

En vez de: "serendipity = serendipia"
Aprende: "One of the greatest scientific discoveries happened by serendipity — Fleming found penicillin by accident."

## Las familias de palabras

Por cada palabra que aprendes, puedes aprender 4-5 más de forma casi gratuita:

- **create** → creator, creation, creative, creativity, recreate
- **happy** → happiness, unhappy, happily, happier, happiest

Aprende la raíz y desbloqueas muchas palabras relacionadas.

## Los prefijos y sufijos más comunes

- **un-**: unhappy, unfair, unbelievable
- **re-**: redo, rewrite, reconsider
- **-tion / -sion**: education, expression, communication
- **-less**: hopeless, careless, useless
- **-ful**: beautiful, careful, hopeful

Con 20 prefijos y sufijos comunes puedes descifrar miles de palabras nuevas.

## Lee, escucha y ve en inglés

La mejor fuente de vocabulario es el input masivo. Suscríbete a newsletters en inglés, sigue cuentas de Instagram en inglés, mira series con subtítulos en inglés.

**Clave:** No busques cada palabra que no conoces. Primero intenta deducirla del contexto. Solo busca las que aparecen más de 3 veces.

## El vocabulario temático

Estudia el vocabulario por temas según tus necesidades:

- Si necesitas inglés de negocios: email vocabulary, meetings, presentations
- Si quieres viajar: airports, hotels, restaurants, emergencies
- Si preparas el FCE/CAE: academic vocabulary, phrasal verbs

## Usa las palabras inmediatamente

Cada vez que aprendes una palabra nueva, úsala en 3 frases diferentes ese mismo día. El uso activo es lo que convierte el vocabulario pasivo (reconocer) en activo (usar).

Ponlo en práctica hoy: ¿cuáles son las 5 palabras en inglés más útiles para tu vida profesional? Búscalas, apréndelas en contexto, y úsalas esta semana.
    `,
  },
  {
    slug: 'perder-miedo-hablar-ingles-publico',
    img: '/media/premios-tiktok-gala.webp',
    cat: 'Consejos',
    title: 'Cómo Perder el Miedo a Hablar Inglés en Público',
    excerpt: 'Supera la vergüenza y gana confianza para hablar inglés en cualquier situación. Técnicas psicológicas y prácticas.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '28 Feb 2024',
    readTime: '7 min',
    keywords: 'miedo hablar inglés, vergüenza inglés, confianza inglés, speaking inglés',
    body: `
## El miedo a hablar en otro idioma es universal

Si tienes miedo de hablar inglés en público, estás en la mejor compañía del mundo. El 75% de los estudiantes de inglés citan el miedo a hablar como su mayor obstáculo. Incluso personas con nivel avanzado bloquean cuando tienen que hablar delante de otros.

La buena noticia: este miedo es aprendido, y por tanto puede desaprenderse.

## Por qué nos da vergüenza

Nuestro cerebro asocia hablar con errores, y los errores con juicio social. Esto activa el sistema de amenaza (amígdala), que nos hace querer huir o quedarnos paralizados. En inglés esto se amplifica porque:

1. No tenemos acceso instantáneo al vocabulario
2. Procesamos más lento que en español
3. Percibimos que "sonamos mal"
4. Nos preocupa qué pensarán los demás

## Técnica 1: La regla del "bueno suficiente"

Permítete ser imperfecto. El objetivo no es hablar sin errores — el objetivo es ser comprendido. Los hablantes nativos no te juzgan por tu acento o gramática imperfecta. Al contrario, suelen admirar que hagas el esfuerzo.

**Cambia el mantra de:** "Tengo que hablar perfecto" **a:** "Solo necesito hacerme entender."

## Técnica 2: Exposición gradual

Como cualquier fobia, la cura es la exposición gradual controlada:

1. Habla inglés solo en casa (hablarte a ti mismo)
2. Habla con aplicaciones de IA
3. Practica con un compañero de intercambio
4. Únete a grupos de conversación online
5. Habla en situaciones de bajo riesgo (tiendas, hostelería)
6. Participa en meetings de trabajo en inglés

## Técnica 3: Prepara frases de emergencia

El pánico llega cuando nos quedamos sin palabras. Aprende estas frases de rescate:

- *"Could you say that again, please?"* — ¿Puedes repetir?
- *"I'm sorry, I didn't catch that."* — No te he entendido.
- *"Let me think about that for a moment."* — Dame un momento.
- *"How do you say... in English?"* — ¿Cómo se dice...?
- *"I know what I mean, but I'm not sure how to say it."*

## Técnica 4: Acepta los silencios

En español, los silencios nos incomodan y tendemos a llenarlos. En inglés, un breve silencio mientras piensas es perfectamente normal y profesional. No necesitas hablar sin parar.

## Técnica 5: Grúpate con otros aprendices

Una de las razones por las que nuestras clases en grupos reducidos son tan efectivas es que todos están en el mismo barco. No hay juicio, hay apoyo mutuo. El entorno seguro acelera enormemente la pérdida del miedo.

## El secreto que nadie te cuenta

La confianza no viene antes de hablar — viene después de haberlo hecho. No esperes sentirte seguro para hablar. Habla, comete errores, supera el momento incómodo, y la confianza llegará sola.

¿Quieres practicar en un entorno seguro con feedback de un profesor nativo? En Speak English Now tenemos el espacio perfecto para ti.
    `,
  },
  {
    slug: 'mejores-apps-aprender-ingles-2024',
    img: '/media/keilin-caroline.webp',
    cat: 'Recursos',
    title: 'Las Mejores Apps para Aprender Inglés en 2024',
    excerpt: 'Revisión honesta y completa de las aplicaciones más útiles para complementar tu aprendizaje de inglés este año.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '20 Feb 2024',
    readTime: '4 min',
    keywords: 'apps aprender inglés, Duolingo, aplicaciones inglés, recursos inglés online',
    body: `
## ¿Pueden las apps enseñarte inglés?

La respuesta honesta: las apps son herramientas complementarias, no sustitutos. Ninguna app puede reemplazar la práctica de conversación real con humanos. Pero usadas estratégicamente, son potentísimos refuerzos.

Aquí tienes nuestra selección honesta para 2024:

## 🥇 Duolingo — La más popular

**Para qué sirve:** Vocabulario básico, gramática introductoria, hábito diario.

**Lo mejor:** Gamificación excelente, totalmente gratuita, interfaz divertida. Perfecta para principiantes que quieren crear el hábito.

**Lo peor:** Las frases son artificiales ("El elefante bebe agua"). No prepara para conversación real. El nivel máximo equivale a un B1 flojo.

**Veredicto:** ✅ Para principiantes absolutos o como calentamiento diario. ❌ No como herramienta principal.

## 🥈 Anki — Para vocabulario serio

**Para qué sirve:** Memorización de vocabulario mediante repetición espaciada científica.

**Lo mejor:** Extremadamente eficaz, personalizable, gratuita. La usan estudiantes de medicina para memorizar miles de términos.

**Lo peor:** La curva de aprendizaje inicial es pronunciada. No es divertida.

**Veredicto:** ✅ Imprescindible si quieres aprender vocabulario serio. Requiere disciplina.

## 🥉 Elsa Speak — Para pronunciación

**Para qué sirve:** Análisis de pronunciación con IA. Te dice exactamente qué sonidos pronuncias mal.

**Lo mejor:** Tecnología de reconocimiento de voz muy precisa. Feedback inmediato y específico.

**Lo peor:** Requiere suscripción de pago. Solo cubre pronunciación.

**Veredicto:** ✅ Muy recomendable si tu objetivo es mejorar la pronunciación. Vale la inversión.

## 🎯 HelloTalk — Para práctica real

**Para qué sirve:** Conectarte con hablantes nativos de inglés que aprenden español.

**Lo mejor:** Práctica de conversación real y gratuita. Intercambio cultural auténtico.

**Lo peor:** La calidad del interlocutor varía. Necesitas llegar con un nivel mínimo.

**Veredicto:** ✅ Excelente complemento cuando tienes nivel A2 o superior.

## 📺 BBC Learning English — Para contexto real

**Para qué sirve:** Podcasts, vídeos y artículos de BBC adaptados para aprendices de inglés.

**Lo mejor:** Contenido real, actualizado, gratuito. Excelente para listening y vocabulario contextual.

**Veredicto:** ✅ Recomendada para nivel B1 en adelante.

## La app que ninguna sustituye: la conversación real

Todas estas apps tienen un límite: no reproducen la presión y espontaneidad de una conversación real. Para eso necesitas clases con profesores nativos.

En Speak English Now complementamos perfectamente el uso de apps con sesiones en vivo donde practicas lo aprendido en conversación real con feedback inmediato.
    `,
  },
  {
    slug: 'ingles-negocios-frases-trabajo',
    img: '/media/berta-caroline.webp',
    cat: 'Gramática',
    title: 'Inglés de Negocios: Frases Esenciales para el Trabajo',
    excerpt: 'Domina el vocabulario y las expresiones profesionales que necesitas para comunicarte con soltura en el ámbito laboral internacional.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '15 Feb 2024',
    readTime: '9 min',
    keywords: 'inglés negocios, inglés trabajo, business english, inglés profesional',
    body: `
## El inglés de negocios: un idioma dentro del idioma

El inglés de negocios tiene sus propias convenciones, fórmulas y registro. Conocerlas marca la diferencia entre parecer amateur o profesional, incluso con nivel intermedio.

## Emails profesionales: estructura y frases

Un email de negocios en inglés sigue esta estructura:

**Apertura formal:**
- *I hope this email finds you well.*
- *I am writing to enquire about...*
- *Following our conversation on...*

**Cuerpo:**
- *Please find attached...*
- *I would like to draw your attention to...*
- *Could you please provide...*

**Cierre:**
- *Please do not hesitate to contact me.*
- *I look forward to hearing from you.*
- *Kind regards / Best regards / Yours sincerely*

## Reuniones y presentaciones

**Para empezar una reunión:**
- *Shall we get started?*
- *Let's go through the agenda.*
- *The main purpose of today's meeting is...*

**Para expresar tu opinión:**
- *From my perspective...*
- *I'd like to point out that...*
- *If I may add...*
- *I see your point, however...*

**Para pedir aclaraciones:**
- *Could you elaborate on that?*
- *What exactly do you mean by...?*
- *I'm not sure I follow. Could you clarify?*

**Para concluir:**
- *To summarize the key points...*
- *Are there any questions?*
- *Let's circle back to this next week.*

## El inglés de las negociaciones

- *What's your budget?*
- *We can be flexible on...*
- *This is our best offer.*
- *Let me take that back to my team.*
- *We're looking for a win-win solution.*
- *Can we meet somewhere in the middle?*

## Frases para llamadas telefónicas y videollamadas

- *Can you hear me clearly?*
- *I'm sorry, you broke up. Could you repeat that?*
- *Let me share my screen.*
- *I'll send you a follow-up email.*
- *Let's schedule our next call.*

## Phrasal verbs de negocios más usados

Los phrasal verbs son inevitables en el inglés profesional:

- **Take over** — hacerse cargo de
- **Follow up** — hacer seguimiento
- **Set up** — configurar / organizar
- **Carry out** — llevar a cabo
- **Come up with** — proponer / idear
- **Scale up** — escalar / crecer
- **Reach out** — ponerse en contacto

## El tono: formal vs informal

El inglés de negocios moderno (especialmente en tecnología y startups) tiende a ser más informal que hace 20 años. Adapta tu tono al contexto:

- **Formal** (sector financiero, legal, gubernamental): más estructura, menos contracciones, vocabulario más sofisticado.
- **Semi-formal** (mayoría de empresas): natural, directo, sin perder profesionalismo.
- **Informal** (startups, tech): tutearse prácticamente, emojis en Slack, directo al grano.

¿Necesitas inglés para tu trabajo o empresa? Ofrecemos formación de inglés de negocios personalizada, bonificable por FUNDAE.
    `,
  },
  {
    slug: 'errores-comunes-espanoles-ingles',
    img: '/media/nominacion-tiktok.webp',
    cat: 'Gramática',
    title: 'Los 15 Errores Más Comunes que Cometen los Españoles en Inglés',
    excerpt: 'Identificamos y explicamos los fallos más frecuentes de los hispanohablantes para que puedas eliminarlos de una vez.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '3 Feb 2024',
    readTime: '7 min',
    keywords: 'errores inglés españoles, fallos inglés, interferencias español inglés',
    body: `
## ¿Por qué cometemos siempre los mismos errores?

Los errores sistemáticos en inglés no son aleatorios: vienen de las diferencias estructurales entre español e inglés. Nuestro cerebro intenta aplicar las reglas del español al inglés, y ahí nacen los "Spanglish mistakes".

Identificarlos es el primer paso para eliminarlos.

## Error 1: Olvidar el sujeto

En español podemos decir "Está lloviendo", pero en inglés el sujeto es obligatorio.
- ❌ *Is raining.* → ✅ *It is raining.*
- ❌ *Is very hot today.* → ✅ *It is very hot today.*

## Error 2: Traducir literalmente los falsos amigos

Los "false friends" son palabras parecidas en español que significan algo distinto:
- *Sensible* (inglés) = razonable/sensato, NO sensible → *Sensitive*
- *Actually* = de hecho/en realidad, NO actualmente → *Currently / Nowadays*
- *Embarrassed* = avergonzado, NO embarazada → *Pregnant*
- *Library* = biblioteca, NO librería → *Bookshop*

## Error 3: El mal uso del Present Perfect

Los españoles tendemos a usar el pasado simple cuando deberíamos usar el presente perfecto.
- ❌ *Did you ever try sushi?* → ✅ *Have you ever tried sushi?*
- ❌ *I just arrived.* → ✅ *I have just arrived.*

## Error 4: "Make" vs "Do"

En español usamos "hacer" para todo, pero en inglés:
- **Make**: crear algo → make a cake, make a decision, make an effort, make a mistake
- **Do**: realizar una actividad → do homework, do exercise, do business, do a favor

## Error 5: "Say" vs "Tell"

- **Say**: simplemente pronunciar → "She said hello."
- **Tell**: comunicar algo A alguien → "She told me a secret." (siempre lleva objeto persona)

## Error 6: Orden de los adjetivos

En inglés los adjetivos siguen un orden fijo: opinión + tamaño + edad + forma + color + origen + material + propósito.
- ✅ *A beautiful small old round green French silver whittling knife*

## Error 7: Preposiciones mal usadas

Las preposiciones no se traducen directamente:
- Pienso **en** ti → I think **of** you (no *about* en este contexto)
- Depende **de** ti → It depends **on** you
- Me casé **con** ella → I married **her** (sin preposición, o "I got married **to** her")

## Error 8: "Boring" vs "Bored"

- **Boring** = aburrido (la causa): *This film is boring.*
- **Bored** = aburrido (el estado): *I am bored.*

Lo mismo con excited/exciting, tired/tiring, interested/interesting.

## Error 9: La doble negación

En español podemos decir "no sé nada". En inglés, la doble negación crea una afirmación.
- ❌ *I don't know nothing.* → ✅ *I don't know anything.* / *I know nothing.*

## Error 10: Omitir el auxiliar en preguntas

- ❌ *You like English?* → ✅ *Do you like English?*
- ❌ *She speaks English?* → ✅ *Does she speak English?*

Estos son los diez más frecuentes, pero la lista completa incluye 15 más. ¿Quieres trabajarlos todos en clase con un profesor nativo? Contáctanos.
    `,
  },
  {
    slug: 'phrasal-verbs-mas-usados-ingles',
    img: '/media/premios-tiktok-escenario.webp',
    cat: 'Vocabulario',
    title: 'Los 50 Phrasal Verbs Más Usados en Inglés (con ejemplos)',
    excerpt: 'Los phrasal verbs son el talón de Aquiles de los hispanohablantes. Aquí tienes los más frecuentes con ejemplos reales de uso.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '25 Jan 2024',
    readTime: '10 min',
    keywords: 'phrasal verbs inglés, verbos frasales inglés, get up, look for, give up',
    body: `
## ¿Por qué son tan difíciles los phrasal verbs?

Los phrasal verbs son combinaciones de un verbo + preposición o adverbio cuyo significado es completamente diferente al del verbo original. No se pueden deducir lógicamente; hay que aprenderlos.

*"Give"* significa dar. *"Give up"* significa rendirse. No hay relación lógica.

Esto los hace especialmente difíciles para hispanohablantes, que no tenemos equivalente directo en español.

## Los 50 phrasal verbs más usados

### Con GET
- **Get up** — levantarse: *I get up at 7 every morning.*
- **Get on** — subir (transporte) / llevarse bien: *Get on the bus. They get on well.*
- **Get off** — bajarse: *Get off at the next stop.*
- **Get over** — superar: *It took me months to get over the breakup.*
- **Get along** — llevarse bien: *Do you get along with your colleagues?*
- **Get away** — escapar: *The thief got away.*
- **Get back** — volver: *When are you getting back?*

### Con LOOK
- **Look for** — buscar: *I'm looking for my keys.*
- **Look after** — cuidar: *Can you look after my dog?*
- **Look forward to** — tener ganas de: *I'm looking forward to the weekend.*
- **Look up** — buscar información: *Look it up in the dictionary.*
- **Look into** — investigar: *We need to look into this issue.*

### Con GIVE
- **Give up** — rendirse / dejar: *Don't give up! She gave up smoking.*
- **Give away** — regalar / revelar: *He gave away the ending!*
- **Give back** — devolver: *Please give back my book.*

### Con TAKE
- **Take off** — despegar / quitarse: *The plane takes off at 10. Take off your shoes.*
- **Take on** — contratar / asumir: *We're taking on new staff.*
- **Take up** — empezar un hobby / ocupar espacio: *I took up yoga. It takes up too much space.*
- **Take away** — llevarse: *They took away my phone.*

### Con PUT
- **Put off** — postponer: *Don't put off till tomorrow what you can do today.*
- **Put up with** — aguantar: *I can't put up with this noise.*
- **Put on** — ponerse ropa / poner (música): *Put on your jacket. Put on some music.*

### Con COME
- **Come up with** — idear: *She came up with a brilliant solution.*
- **Come across** — encontrar por casualidad: *I came across this photo.*
- **Come back** — volver: *Come back tomorrow.*
- **Come out** — salir (resultado) / publicarse: *The results came out yesterday.*

### Con TURN
- **Turn on/off** — encender/apagar: *Turn on the lights. Turn off the TV.*
- **Turn up** — aparecer / subir volumen: *He didn't turn up. Turn up the volume.*
- **Turn down** — rechazar / bajar: *She turned down the job offer.*

### Con BREAK
- **Break down** — averiarse / derrumbarse emocionalmente: *The car broke down.*
- **Break up** — romper (pareja): *They broke up last year.*
- **Break in** — entrar ilegalmente: *Someone broke in last night.*
- **Break out** — escapar / estallar: *A fire broke out.*

## Cómo aprenderlos eficazmente

1. **Apréndelos en frases, no aislados.** El contexto es todo.
2. **Agrúpalos por verbo base.** Así construyes patrones.
3. **Úsalos activamente.** Escribe 3 frases con cada uno.
4. **Exponte al input real.** Escucha podcasts y series; los nativos los usan constantemente.

En nuestras clases online dedicamos tiempo específico a los phrasal verbs en conversación real. ¡Únete y empieza a usarlos de forma natural!
    `,
  },
  {
    slug: 'ingles-britanico-vs-americano-diferencias',
    img: '/media/poster-vsl.webp',
    cat: 'Cultura',
    title: 'Inglés Británico vs Americano: Diferencias que Todo Estudiante Debe Conocer',
    excerpt: 'Pronunciación, vocabulario, gramática y cultura: todo lo que distingue el inglés del Reino Unido del de Estados Unidos.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '15 Jan 2024',
    readTime: '6 min',
    keywords: 'inglés británico, inglés americano, diferencias inglés, British English, American English',
    body: `
## ¿Cuál deberías aprender?

La primera pregunta que hacen muchos estudiantes es: ¿inglés británico o americano? La respuesta práctica: ambos son igualmente válidos y mutuamente comprensibles. Elige el que más se adapte a tus necesidades.

Sin embargo, conocer las diferencias es esencial para no llevarte sorpresas.

## Diferencias de vocabulario

Algunas palabras son completamente distintas:

| Español | Británico | Americano |
|---------|-----------|-----------|
| Apartamento | flat | apartment |
| Ascensor | lift | elevator |
| Coche | car / motor | car |
| Otoño | autumn | fall |
| Vacaciones | holiday | vacation |
| Patatas fritas (bolsa) | crisps | chips |
| Patatas fritas (restaurante) | chips | french fries |
| Galleta | biscuit | cookie |
| Teléfono móvil | mobile | cell phone |
| Gasolina | petrol | gas |

## Diferencias de pronunciación

- La **"r"** final: en británico no se pronuncia (*car* = "ca"), en americano sí se pronuncia con "r" retroflexa.
- La **"t"** entre vocales: en americano se convierte en "d" (*water* = "wader"), en británico se mantiene.
- La **"a"** en palabras como *bath, path, glass*: en inglés RP (received pronunciation) es una "aa" larga, en americano es "æ" corta.

## Diferencias ortográficas

| Tipo | Británico | Americano |
|------|-----------|-----------|
| -our / -or | colour, favour | color, favor |
| -re / -er | centre, theatre | center, theater |
| -ise / -ize | organise, realise | organize, realize |
| -ence / -ense | defence, licence | defense, license |
| -ll / -l | travelling, cancelled | traveling, canceled |

## Diferencias gramaticales

**Present Perfect vs Simple Past:**
El británico usa mucho más el presente perfecto; el americano prefiere el pasado simple.
- 🇬🇧 *I've just eaten.* → 🇺🇸 *I just ate.*
- 🇬🇧 *Have you seen that film yet?* → 🇺🇸 *Did you see that movie yet?*

**"Shall" para futuro:** Común en británico (*Shall we go?*), raro en americano (*Should we go? / Let's go?*).

## Para el FCE y el CAE

Los exámenes Cambridge (que ofrecemos en nuestro centro autorizado) aceptan tanto el inglés británico como el americano, siempre que seas consistente. No mezcles en el mismo texto *colour* (británico) con *organize* (americano).

En nuestras clases te enseñamos a manejar ambas variedades con soltura.
    `,
  },
  {
    slug: 'preparar-examen-ielts-fce',
    img: '/media/caroline-tiktok.webp',
    cat: 'Consejos',
    title: 'Cómo Preparar el FCE y el IELTS: Guía Definitiva',
    excerpt: 'Todo lo que necesitas saber para superar los exámenes de inglés más reconocidos internacionalmente, con estrategias para cada parte.',
    author: 'Caroline Franks',
    authorRole: 'Fundadora & Profesora',
    avatar: '/media/caroline-avatar.webp',
    date: '5 Jan 2024',
    readTime: '11 min',
    keywords: 'FCE, IELTS, examen inglés Cambridge, preparación FCE, B2 inglés',
    body: `
## ¿Por qué obtener una certificación oficial de inglés?

Un certificado oficial de inglés abre puertas: universidades, oportunidades laborales en España y en el extranjero, visados, posgrados internacionales. Los más reconocidos son:

- **FCE (B2 First)**: Nivel B2. El más popular en España para jóvenes y universitarios.
- **CAE (C1 Advanced)**: Nivel C1. Requerido por muchas universidades top.
- **IELTS**: Reconocido globalmente, especialmente en UK, Australia y Canadá.
- **TOEFL**: Más orientado a universidades americanas.

En Speak English Now ofrecemos preparación específica para FCE y CAE.

## Estructura del FCE (B2 First)

El FCE tiene 5 partes:

**1. Reading & Use of English (75 min)**
- Textos de comprensión lectora
- Gramática y vocabulario en contexto
- Word formation, key word transformations

**2. Writing (80 min)**
- Parte 1: Un ensayo argumentativo (obligatorio)
- Parte 2: Elección entre email, artículo, historia o reseña

**3. Listening (40 min)**
- 4 partes: monólogos, entrevistas, conversaciones múltiples

**4. Speaking (14 min, en pareja)**
- Parte 1: Preguntas personales
- Parte 2: Comparar fotografías
- Parte 3: Tarea colaborativa
- Parte 4: Discusión

## Estrategias para el FCE

**Para Reading:**
- Lee siempre el título y el contexto antes del texto completo
- En las preguntas de múltiples opciones, busca primero las incorrectas
- No te detengas en vocabulario desconocido; dedúcelo del contexto

**Para Writing:**
- El ensayo siempre tiene estructura: intro + 2-3 párrafos de argumentos + conclusión
- Variedad de vocabulario y estructuras gramaticales es lo que más puntúa
- Respeta el límite de palabras (escribe entre el 90-110% del límite)

**Para Listening:**
- Lee las preguntas ANTES de escuchar
- El audio va de principio a fin y no se repite; prepárate
- Confía en tu primera impresión

**Para Speaking:**
- Interactúa con tu compañero; el examinador evalúa interacción, no monólogos
- Si no sabes una palabra, explícala con otras: parafrasear también puntúa
- Usa conectores: *Furthermore, On the other hand, As a result...*

## Estructura del IELTS

**Academic (para universidad) vs General Training (para trabajo/emigración)**

**1. Listening (30 min)** — Similar al FCE listening

**2. Reading (60 min)**
- 3 textos largos
- 14 tipos de preguntas diferentes (True/False/Not Given, Matching headings, etc.)

**3. Writing (60 min)**
- Tarea 1: Describir un gráfico, tabla o proceso (150 palabras)
- Tarea 2: Ensayo de opinión/argumento (250 palabras)

**4. Speaking (11-14 min)**
- Parte 1: Preguntas personales (4-5 min)
- Parte 2: Hablar 1-2 min sobre un tema (tarjeta de tópico)
- Parte 3: Discusión sobre el tema de la parte 2

## Plan de preparación de 3 meses

**Mes 1:** Diagnóstico y bases
- Haz un examen de práctica completo sin preparación previa
- Identifica tus puntos débiles
- Trabaja gramática y vocabulario

**Mes 2:** Estrategias y práctica intensiva
- Aprende las estrategias específicas de cada sección
- 2-3 exámenes de práctica por semana
- Feedback de profesor nativo

**Mes 3:** Simulacros y puesta a punto
- Un simulacro completo por semana en condiciones reales
- Trabaja solo tus puntos débiles
- Ensaya el speaking con un profesor

¿Quieres preparar tu FCE o CAE con nosotros? Somos centro autorizado Cambridge con grupos reducidos y profesora nativa.
    `,
  },
];

const currentSlugs = new Set([featured, ...currentArticles].map(article => article.slug));

export const articles = [
  ...currentArticles,
  ...legacyArticles.filter(article => !currentSlugs.has(article.slug)).map(article => ({
    ...article,
    img: legacyImageMap[article.img]?.full || article.img,
    cardImg: legacyImageMap[article.img]?.card || article.img,
  })),
];

export const categories = ['Todos', 'Pronunciación', 'Gramática', 'Vocabulario', 'Cultura', 'Consejos', 'Recursos'];

export const allArticles = [featured, ...articles];
